import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/checkout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { Trash2, Eye, EyeOff, LogOut } from "lucide-react";
import { T } from "@/constants/testIds";

export default function AdminDashboard() {
  const nav = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("db_admin_token") : null;
  const email = typeof window !== "undefined" ? localStorage.getItem("db_admin_email") : null;
  const client = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

  const [leads, setLeads] = useState([]);
  const [sales, setSales] = useState({ transactions: [], summary: {} });
  const [testimonials, setTestimonials] = useState([]);
  const [newT, setNewT] = useState({ author: "", role: "", text: "", rating: 5 });

  const load = async () => {
    try {
      const [l, s, t] = await Promise.all([
        client.get("/admin/leads"),
        client.get("/admin/sales"),
        client.get("/admin/testimonials"),
      ]);
      setLeads(l.data || []);
      setSales(s.data || { transactions: [], summary: {} });
      setTestimonials(t.data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Sessão expirada");
        logout();
      } else {
        toast.error("Falha ao carregar dados");
      }
    }
  };

  const logout = () => {
    localStorage.removeItem("db_admin_token");
    localStorage.removeItem("db_admin_email");
    nav("/admin");
  };

  useEffect(() => {
    if (!token) { nav("/admin"); return; }
    load();
    // eslint-disable-next-line
  }, []);

  const createTestimonial = async (e) => {
    e.preventDefault();
    try {
      await client.post("/admin/testimonials", newT);
      setNewT({ author: "", role: "", text: "", rating: 5 });
      toast.success("Depoimento publicado");
      load();
    } catch {
      toast.error("Erro ao salvar");
    }
  };

  const toggle = async (id) => {
    try { await client.post(`/admin/testimonials/${id}/toggle`); load(); } catch { toast.error("Erro"); }
  };
  const del = async (id) => {
    try { await client.delete(`/admin/testimonials/${id}`); toast.success("Removido"); load(); } catch { toast.error("Erro"); }
  };

  const exportCSV = () => {
    const rows = [["name", "email", "created_at"]].concat(
      leads.map((l) => [l.name, l.email, l.created_at])
    );
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "leads.csv";
    a.click();
  };

  return (
    <div className="min-h-screen bg-black grain">
      <header className="border-b border-white/5 py-5 px-5 md:px-10 flex items-center justify-between">
        <Logo className="h-7 w-7" />
        <div className="flex items-center gap-4 text-[12px] font-mono tracking-[0.2em] uppercase text-neutral-500">
          <span className="hidden md:inline">{email}</span>
          <Button
            data-testid={T.adminLogout}
            onClick={logout}
            variant="ghost"
            className="text-neutral-300 hover:text-red-500 hover:bg-transparent gap-2"
          >
            <LogOut className="h-4 w-4" /> Sair
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-5 md:px-10 py-10">
        <h1 className="font-display font-black text-neutral-50 text-5xl md:text-6xl leading-none mb-10">
          Painel <span className="text-red-600 italic">editorial</span>.
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-10">
          <Stat label="Leads capturados" value={leads.length} />
          <Stat label="Transações" value={sales.summary?.total_count || 0} />
          <Stat label="Vendas pagas" value={sales.summary?.paid_count || 0} />
          <Stat
            label="Receita (BRL)"
            value={`R$ ${(((sales.summary?.total_paid_cents) || 0) / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`}
          />
        </div>

        <Tabs defaultValue="testimonials">
          <TabsList className="bg-[#0a0a0a] border border-white/10 rounded-none h-12 p-1">
            <TabsTrigger value="testimonials" className="rounded-none data-[state=active]:bg-red-600 data-[state=active]:text-white font-mono text-[11px] tracking-widest uppercase px-5">Depoimentos</TabsTrigger>
            <TabsTrigger value="leads" className="rounded-none data-[state=active]:bg-red-600 data-[state=active]:text-white font-mono text-[11px] tracking-widest uppercase px-5">Leads</TabsTrigger>
            <TabsTrigger value="sales" className="rounded-none data-[state=active]:bg-red-600 data-[state=active]:text-white font-mono text-[11px] tracking-widest uppercase px-5">Vendas</TabsTrigger>
          </TabsList>

          <TabsContent value="testimonials" className="mt-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <form onSubmit={createTestimonial} className="lg:col-span-5 border border-white/10 p-6 bg-[#0a0a0a]">
                <h3 className="font-display text-xl text-neutral-50 mb-5">Publicar depoimento real</h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-neutral-400 text-[11px] uppercase tracking-widest">Autor</Label>
                    <Input value={newT.author} onChange={(e) => setNewT({ ...newT, author: e.target.value })} required className="bg-black border-white/10 h-11 mt-1" />
                  </div>
                  <div>
                    <Label className="text-neutral-400 text-[11px] uppercase tracking-widest">Função / cidade (opcional)</Label>
                    <Input value={newT.role} onChange={(e) => setNewT({ ...newT, role: e.target.value })} className="bg-black border-white/10 h-11 mt-1" />
                  </div>
                  <div>
                    <Label className="text-neutral-400 text-[11px] uppercase tracking-widest">Texto</Label>
                    <Textarea value={newT.text} onChange={(e) => setNewT({ ...newT, text: e.target.value })} required rows={4} className="bg-black border-white/10 mt-1" />
                  </div>
                  <div>
                    <Label className="text-neutral-400 text-[11px] uppercase tracking-widest">Avaliação (1-5)</Label>
                    <Input type="number" min={1} max={5} value={newT.rating} onChange={(e) => setNewT({ ...newT, rating: Number(e.target.value) })} className="bg-black border-white/10 h-11 mt-1" />
                  </div>
                  <Button type="submit" className="w-full bg-red-600 hover:bg-red-700 h-11 rounded-sm font-mono tracking-widest text-[11px] uppercase">Publicar</Button>
                </div>
                <p className="mt-4 text-[11px] text-neutral-500 leading-relaxed">
                  Importante: publique somente depoimentos reais com autorização. Nunca fabrique comentários.
                </p>
              </form>
              <div className="lg:col-span-7 border border-white/10 p-6 bg-[#0a0a0a]">
                <h3 className="font-display text-xl text-neutral-50 mb-5">Lista ({testimonials.length})</h3>
                {testimonials.length === 0 && <p className="text-neutral-500 text-sm">Nenhum depoimento ainda.</p>}
                <div className="space-y-3">
                  {testimonials.map((t) => (
                    <div key={t.id} className="border border-white/10 p-4 flex items-start justify-between gap-4">
                      <div>
                        <div className="font-display text-neutral-100">{t.author}</div>
                        <div className="font-mono text-[10px] tracking-widest uppercase text-neutral-500 mb-2">{t.role}</div>
                        <p className="text-neutral-400 text-[13px]">{t.text}</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button variant="ghost" size="icon" onClick={() => toggle(t.id)}>
                          {t.published ? <Eye className="h-4 w-4 text-red-500" /> : <EyeOff className="h-4 w-4 text-neutral-500" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => del(t.id)}>
                          <Trash2 className="h-4 w-4 text-neutral-400 hover:text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="leads" className="mt-8">
            <div className="border border-white/10 bg-[#0a0a0a]">
              <div className="flex items-center justify-between p-5 border-b border-white/5">
                <h3 className="font-display text-xl text-neutral-50">Leads capturados ({leads.length})</h3>
                <Button onClick={exportCSV} className="bg-red-600 hover:bg-red-700 rounded-sm font-mono text-[11px] tracking-widest uppercase">Exportar CSV</Button>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-mono tracking-widest uppercase text-neutral-500">
                    <th className="p-4">Nome</th>
                    <th className="p-4">E-mail</th>
                    <th className="p-4">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className="border-t border-white/5">
                      <td className="p-4 text-neutral-100">{l.name}</td>
                      <td className="p-4 text-neutral-300">{l.email}</td>
                      <td className="p-4 text-neutral-500 font-mono text-xs">
                        {new Date(l.created_at).toLocaleString("pt-BR")}
                      </td>
                    </tr>
                  ))}
                  {leads.length === 0 && (
                    <tr><td colSpan={3} className="p-6 text-center text-neutral-500">Nenhum lead ainda.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="sales" className="mt-8">
            <div className="border border-white/10 bg-[#0a0a0a]">
              <div className="p-5 border-b border-white/5">
                <h3 className="font-display text-xl text-neutral-50">Transações ({sales.transactions.length})</h3>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-mono tracking-widest uppercase text-neutral-500">
                    <th className="p-4">Session</th>
                    <th className="p-4">Status</th>
                    <th className="p-4">Pagamento</th>
                    <th className="p-4">Valor</th>
                    <th className="p-4">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.transactions.map((t) => (
                    <tr key={t.session_id} className="border-t border-white/5">
                      <td className="p-4 font-mono text-xs text-neutral-500">{t.session_id?.slice(0, 22)}…</td>
                      <td className="p-4 text-neutral-300">{t.status}</td>
                      <td className="p-4">
                        <span className={t.payment_status === "paid" ? "text-red-500" : "text-neutral-500"}>
                          {t.payment_status}
                        </span>
                      </td>
                      <td className="p-4 text-neutral-200">R$ {((t.amount || 0) / 100).toFixed(2)}</td>
                      <td className="p-4 text-neutral-500 font-mono text-xs">
                        {t.created_at ? new Date(t.created_at).toLocaleString("pt-BR") : "—"}
                      </td>
                    </tr>
                  ))}
                  {sales.transactions.length === 0 && (
                    <tr><td colSpan={5} className="p-6 text-center text-neutral-500">Nenhuma transação ainda.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-5">
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-red-500 mb-3">{label}</div>
      <div className="font-display text-3xl text-neutral-50">{value}</div>
    </div>
  );
}
